import { MEDIA_QUERIES } from "@src/constants/media-queries";
import { type SharedData, useApplicationContext } from "@src/context";
import { useFilterContext } from "@src/context/FilterContext";
import { useProviderListContext } from "@src/context/ProviderListContext";
import { useMediaQuery } from "@uhg-abyss/web/hooks/useMediaQuery";
import { Chip } from "@uhg-abyss/web/ui/Chip";
import { Divider } from "@uhg-abyss/web/ui/Divider";
import { Layout } from "@uhg-abyss/web/ui/Layout";
import { Text } from "@uhg-abyss/web/ui/Text";
import { Tooltip } from "@uhg-abyss/web/ui/Tooltip";
import { isValidElement, type ReactElement, type ReactNode } from "react";
import { shouldExcludeCareFormatFromDisplay } from "../utils/provider-filter-utils";

const CHIP_STYLE = {
  borderWidth: "1px",
  borderStyle: "solid",
  borderColor: "$app.color.active.filter.chip.border",
  fontSize: "12px",
} as const;

const CHIP_ICON_STYLE = {
  width: "12px",
  height: "12px",
  fontSize: "12px !important",
  padding: "0",
  cursor: "pointer",
} as const;

const TOOLTIP_CHIP_CSS_BASE = {
  borderWidth: "1px",
  borderStyle: "solid",
  borderColor: "$app.color.active.filter.chip.border",
  fontSize: "12px",
  cursor: "default",
  backgroundColor: "$app.color.surface.container.secondary",
  ".abyss-chip-text": {
    whiteSpace: "normal",
    overflowWrap: "anywhere",
    wordBreak: "break-word",
    lineHeight: "1",
  },
} as const;

const TOOLTIP_CHIP_OVERFLOW_CSS = {
  padding: "15px 8px",
} as const;

const TOOLTIP_MORE_CHIP_CSS = {
  borderWidth: "1px",
  borderStyle: "solid",
  borderColor: "$app.color.active.filter.chip.border",
  fontSize: "12px",
  color: "$app.color.text.link.rest.primary",
  cursor: "pointer",
  ".abyss-icon-symbol": { display: "none" },
} as const;

const CLEAR_ALL_CHIP_CSS = {
  borderWidth: "1px",
  borderStyle: "solid",
  borderColor: "$app.color.active.filter.chip.border",
  textDecoration: "underline",
  fontSize: "12px",
  ".abyss-icon-symbol": {
    width: "75px",
    height: "15px",
    fontSize: "0px !important",
    marginLeft: "-75px",
    position: "relative",
  },
} as const;

const ACTIVE_FILTERS_ROW_STYLE = {
  display: "flex",
  flexDirection: "row",
  alignItems: "flex-end",
} as const;

const CHIP_STACK_CSS = {
  flexDirection: "row",
  gap: "8px",
  paddingTop: "8px",
  flexWrap: "wrap",
} as const;

const TOOLTIP_CHIPS_WRAPPER_STYLE = {
  wordWrap: "break-word",
  display: "flex",
  flexWrap: "wrap",
  gap: "8px",
} as const;

const CLEAR_ALL_WRAPPER_STYLE = {
  display: "flex",
  alignItems: "center",
} as const;

const NON_CLOSABLE_CHIP_CSS = {
  ".abyss-chip-close-button": {
    display: "none !important",
  },
} as const;

const formatHourChipLabel = (hour: number): string => {
  const suffix = hour >= 12 ? "PM" : "AM";
  const normalizedHour = hour % 12 === 0 ? 12 : hour % 12;
  return `${normalizedHour}:00 ${suffix}`;
};

const hasLongTooltipText = (text: string): boolean => text.length > 50;

interface ActiveFiltersProps {
  isMobile: boolean;
  isMediumMax: boolean;
  isWideTabletMax: boolean;
  onProviderSearchRefresh: (appData: SharedData) => Promise<void>;
}

interface FilterChipElementProps {
  className?: string;
  text?: string;
  "data-filter-category"?: string;
  "data-filter-value"?: string;
}

const isFilterChipElement = (chip: ReactNode): chip is ReactElement<FilterChipElementProps> => {
  return isValidElement<FilterChipElementProps>(chip);
};

export const ActiveFilters = ({
  isMobile,
  isMediumMax,
  isWideTabletMax,
  onProviderSearchRefresh,
}: ActiveFiltersProps) => {
  const { primaryFilter, secondaryFilter, updateSecondaryFilter, resetSecondaryFilters } =
    useFilterContext();
  const {
    data: applicationData,
    setData: setApplicationData,
    removeValueFromKey,
  } = useApplicationContext();
  const { loading } = useProviderListContext();
  const isTablet = useMediaQuery(MEDIA_QUERIES.tabletRange);

  const isSharedDataArray = (value: SharedData[keyof SharedData]): value is Array<string> => {
    return Array.isArray(value);
  };

  // Helper function to handle removal logic for sessionType & providerGender
  const updateFilterValue = (
    filterValue: any,
    valueToRemove: string,
    applicationDataKey: keyof SharedData,
  ): void => {
    if (Array.isArray(filterValue)) {
      const remainingValues = filterValue.filter((item: string) => {
        return item !== valueToRemove;
      });
      if (remainingValues.length === 1) {
        setApplicationData(applicationDataKey as keyof SharedData, remainingValues[0]);
      } else if (remainingValues.length === 0) {
        setApplicationData(applicationDataKey as keyof SharedData, "no preference");
      }
    } else if (filterValue === valueToRemove) {
      setApplicationData(applicationDataKey as keyof SharedData, "no preference");
    }
  };

  const getNextValue = (currentValue: any, valueToRemove: string) => {
    if (Array.isArray(currentValue)) {
      const remaining = currentValue.filter((item: string) => {
        return item !== valueToRemove;
      });
      if (remaining.length === 0) return "no preference";
      if (remaining.length === 1) return remaining[0];
      return remaining;
    }
    return currentValue === valueToRemove ? "no preference" : currentValue;
  };

  // Handle chip close — updates filter context and re-fetches providers
  const handleChipClose = async (category: string, value: string) => {
    // First, update the filter context (this removes the value)
    updateSecondaryFilter(category, value);

    const nextAppData: Partial<SharedData> & Record<string, any> = { ...applicationData };

    // Then, determine what value should be in the application context based on remaining filters
    if (category === "sessionType") {
      const nextValue = getNextValue(secondaryFilter.sessionType, value);
      updateFilterValue(secondaryFilter.sessionType, value, "sessionType");
      nextAppData.sessionType = nextValue;
    } else if (category === "providerGender") {
      const nextValue = getNextValue(secondaryFilter.providerGender, value);
      updateFilterValue(secondaryFilter.providerGender, value, "providerGender");
      nextAppData.providerGender = nextValue;
    } else if (category === "specialityMatchType") {
      setApplicationData("specialityMatchType", "or");
      nextAppData.specialityMatchType = "or";
    } else {
      // For other array-based filters, update directly
      const sharedKey = category as keyof SharedData;
      removeValueFromKey(sharedKey, value);
      const currentValue = nextAppData[sharedKey];
      if (isSharedDataArray(currentValue)) {
        nextAppData[sharedKey] = currentValue.filter((item) => {
          return item !== value;
        });
      } else if (typeof currentValue === "number") {
        const parsedValue = Number(value);
        if (Number.isFinite(parsedValue) && currentValue === parsedValue) {
          setApplicationData(sharedKey, undefined);
          nextAppData[sharedKey] = undefined;
        }
      } else if (currentValue === value) {
        nextAppData[sharedKey] = "";
      }
    }
    // Finally, trigger the API call to refresh provider list
    await onProviderSearchRefresh(nextAppData);
  };

  const handleClearAllFilters = () => {
    resetSecondaryFilters();
    setApplicationData("specialities", []);
    setApplicationData("specialityMatchType", "or");
    // setApplicationData("modalities", []);
    setApplicationData("languages", "");
    // careFormat intentionally not cleared — required filter, mirrors providerType behavior
    setApplicationData("providerGender", "");
    setApplicationData("providerRadius", "");
    setApplicationData("dayOfWeek", []);
    setApplicationData("startHour", undefined);
    setApplicationData("endHour", undefined);
    // sessionType is locked when under-12 in-person is active — preserve it
    if (!applicationData.isUnder12InPersonProceed) {
      setApplicationData("sessionType", "no preference");
    }
    // Trigger API call with reset filters
    void onProviderSearchRefresh({
      ...applicationData,
      specialities: [],
      specialityMatchType: "or",
      languages: "",
      // careFormat not overridden — ...applicationData already carries the original value
      providerGender: "",
      dayOfWeek: [],
      startHour: undefined,
      endHour: undefined,
      sessionType: applicationData.isUnder12InPersonProceed
        ? applicationData.sessionType
        : "no preference",
      providerRadius: "",
    });
  };

  // Helper to determine if close icon should be hidden
  const shouldHideCloseIcon = (
    category: string,
    value: string,
    filterType: "primary" | "secondary" = "secondary",
  ): boolean => {
    if (filterType === "primary") {
      return true;
    }
    // Hide close icon for certain categories in secondary/tooltip chips
    return (
      category === "providerType" ||
      category === "ageRange" ||
      category === "insurancePolicy" ||
      category === "careFormat" ||
      (category === "sessionType" && applicationData.isUnder12InPersonProceed === true)
    );
  };

  // Helper to create a closeable chip
  const createChipElement = (
    category: string,
    value: string,
    filterType: "primary" | "secondary",
    text = value,
  ) => {
    const isNonClosable = shouldHideCloseIcon(category, value, filterType);

    return (
      <Chip
        maxWidth={100}
        key={`${category}-${value}`}
        className={isNonClosable ? "non-closable" : ""}
        text={text}
        data-filter-category={category}
        data-filter-value={value}
        onClose={
          isNonClosable || loading
            ? undefined
            : () => {
                handleChipClose(category, value);
              }
        }
        css={{
          ...CHIP_STYLE,
          ...(isNonClosable ? NON_CLOSABLE_CHIP_CSS : {}),
          ".abyss-icon-symbol": {
            ...CHIP_ICON_STYLE,
            display: "flex",
          },
        }}
      />
    );
  };

  // Helper to build secondary filter chips
  const buildSecondaryChips = () => {
    const chips: ReturnType<typeof createChipElement>[] = [];

    // When the under-12 in-person flag is active, always show the locked "In-person" chip
    if (applicationData.isUnder12InPersonProceed) {
      chips.push(createChipElement("sessionType", "In-person", "secondary"));
    }

    return [
      ...chips,
      ...Object.entries(secondaryFilter).flatMap(([category, values]) => {
        // Skip sessionType when the locked chip is already injected above
        if (category === "sessionType" && applicationData.isUnder12InPersonProceed) {
          return [];
        }
        if (
          shouldExcludeCareFormatFromDisplay(secondaryFilter, isWideTabletMax) &&
          category === "careFormat"
        ) {
          return [];
        }
        // Handle array values
        if (Array.isArray(values) && values.length > 0) {
          return values.map((value) => {
            return createChipElement(category, value, "secondary");
          });
        }
        if (category === "specialityMatchType") {
          return [];
        }
        // Handle single string/number values
        if (values && !Array.isArray(values) && values !== "" && values !== "no preference") {
          const chipText =
            (category === "startHour" || category === "endHour") && typeof values === "number"
              ? formatHourChipLabel(values)
              : String(values);
          return [createChipElement(category, String(values), "secondary", chipText)];
        }
        return [];
      }),
    ];
  };

  // Helper to build primary filter chips
  const buildPrimaryChips = () => {
    if (isMobile) {
      return [
        primaryFilter.ageRange && createChipElement("ageRange", primaryFilter.ageRange, "primary"),
        primaryFilter.typeOfService &&
          createChipElement("typeOfService", primaryFilter.typeOfService, "primary"),
      ].filter(Boolean);
    }
    if (isTablet) {
      return [
        primaryFilter.ageRange && createChipElement("ageRange", primaryFilter.ageRange, "primary"),
      ].filter(Boolean);
    }
    return [];
  };

  // Helper to build insurance filter chip
  const buildInsuranceChip = () => {
    if (isMediumMax) {
      return primaryFilter.insurancePolicy
        ? [createChipElement("insurancePolicy", primaryFilter.insurancePolicy, "primary")]
        : [];
    }
    return [];
  };

  // Helper to render tooltip chip content
  const renderTooltipChipsContent = (remainingChips: ReactNode[]) => {
    return (
      <div style={TOOLTIP_CHIPS_WRAPPER_STYLE}>
        {remainingChips.map((chip) => {
          if (!isFilterChipElement(chip)) return null;
          const category = chip.props["data-filter-category"] ?? String(chip.key);
          const value = chip.props["data-filter-value"] ?? chip.props.text ?? "";
          const text = chip.props.text;
          const isNonClosable = chip.props.className === "non-closable";
          const hideCloseIcon = isNonClosable || shouldHideCloseIcon(category, value);
          return (
            <Chip
              key={`${category}-${value}`}
              onClose={
                hideCloseIcon
                  ? undefined
                  : () => {
                      handleChipClose(category, value);
                    }
              }
              text={text ?? ""}
              css={{
                ...TOOLTIP_CHIP_CSS_BASE,
                ...(hasLongTooltipText(text ?? "") ? TOOLTIP_CHIP_OVERFLOW_CSS : {}),
                ...(hideCloseIcon ? NON_CLOSABLE_CHIP_CSS : {}),
                ".abyss-icon-symbol": {
                  ...CHIP_ICON_STYLE,
                  display: "flex",
                },
              }}
            />
          );
        })}
      </div>
    );
  };

  // Main renderFilterChips — builds, sorts and slices visible chips
  const renderFilterChips = () => {
    const allChips = [...buildSecondaryChips(), ...buildPrimaryChips(), ...buildInsuranceChip()];
    // Sort: non-closable chips first
    const sortedChips = allChips.filter(isFilterChipElement).sort((a, b) => {
      const aNonClosable = a.props?.className === "non-closable";
      const bNonClosable = b.props?.className === "non-closable";
      if (aNonClosable === bNonClosable) {
        return 0;
      }
      return aNonClosable ? -1 : 1;
    });
    const visibleChips = sortedChips.slice(0, 5);
    const remainingChips = sortedChips.slice(5);
    const remainingCount = remainingChips.length;

    if (remainingCount > 0) {
      visibleChips.push(
        <Tooltip
          key="more-filters-tooltip"
          data-testid="test-id-active-filters-more-filters-tooltip"
          content={renderTooltipChipsContent(remainingChips)}
        >
          <Chip key="more-filters" text={`+${remainingCount}`} css={TOOLTIP_MORE_CHIP_CSS} />
        </Tooltip>,
      );
    }

    return visibleChips;
  };

  // Count secondary filters (includes all — used for header count)
  const countSecondaryFilters = (): number => {
    // Count the injected locked "In-person" chip when the flag is active
    const under12Bonus = applicationData.isUnder12InPersonProceed ? 1 : 0;

    return (
      under12Bonus +
      Object.entries(secondaryFilter).reduce((count, [category, values]) => {
        if (
          shouldExcludeCareFormatFromDisplay(secondaryFilter, isWideTabletMax) &&
          category === "careFormat"
        ) {
          return count;
        }
        if (category === "specialityMatchType") {
          return count;
        }
        // Skip sessionType from the regular count when already counted via the injected chip
        if (category === "sessionType" && applicationData.isUnder12InPersonProceed) {
          return count;
        }
        if (Array.isArray(values)) {
          return count + values.length;
        }
        if (values && !Array.isArray(values) && values !== "" && values !== "no preference") {
          return count + 1;
        }
        return count;
      }, 0)
    );
  };

  // Count only filters that can be cleared (excludes non-removable providerType and careFormat)
  const countClearableFilters = (): number => {
    const NON_REMOVABLE_FILTERS = new Set(["providerType", "careFormat"]);
    return Object.entries(secondaryFilter).reduce((count, [key, values]) => {
      if (NON_REMOVABLE_FILTERS.has(key)) return count;
      if (key === "specialityMatchType") return count;
      // sessionType is locked and non-removable when under-12 in-person is active
      if (key === "sessionType" && applicationData.isUnder12InPersonProceed) return count;
      if (Array.isArray(values)) {
        return count + values.length;
      }
      if (values && !Array.isArray(values) && values !== "" && values !== "no preference") {
        return count + 1;
      }
      return count;
    }, 0);
  };

  // Combines primary and secondary filter counts
  const computeFilterCount = (): number => {
    const secondaryCount = countSecondaryFilters();
    let primaryCount = 0;
    if (isTablet) primaryCount = 1;
    else if (isMobile) primaryCount = 2;
    if (isMediumMax) primaryCount += 1;
    return secondaryCount + primaryCount;
  };

  const filterCount = computeFilterCount();
  if (filterCount === 0 && countClearableFilters() === 0) return null;

  return (
    <div>
      <Text css={{ fontWeight: 700, marginBottom: "14px" }}>
        {`${isMobile ? "Active Filters" : "Additional active filters"} (${String(filterCount)}):`}
      </Text>
      <div style={ACTIVE_FILTERS_ROW_STYLE}>
        <Chip.Group title="Chip Group">
          <Layout.Stack css={CHIP_STACK_CSS}>
            {renderFilterChips()}
            {countClearableFilters() > 0 && (
              <div style={CLEAR_ALL_WRAPPER_STYLE}>
                <Divider orientation="vertical" height="24px" />
                <Chip
                  onClick={() => {
                    handleClearAllFilters();
                  }}
                  text={"Clear all filters"}
                  css={{
                    ...CLEAR_ALL_CHIP_CSS,
                    ...NON_CLOSABLE_CHIP_CSS,
                    "&:hover": { cursor: "pointer" },
                  }}
                />
              </div>
            )}
          </Layout.Stack>
        </Chip.Group>
      </div>
    </div>
  );
};
